import { useState, useEffect } from 'react';

import { api, useAPIContext } from '../../../api/context';
import { useFPContext } from '../../../utils/fingerprint';
import dialogs from '../../../dialogs';
import toast from '../../../utils/toast';
import Base from '../../Base';
import styles from './UrlSubmit.module.scss';

const extractTikTokVideoId = (url) => {
  const match = url.match(/video\/(\d+)/);
  return match ? match[1] : null;
};

const UrlSubmit = ({ campaign, activity, handle }) => {

  const { token } = useAPIContext();

  const { setDialog } = dialogs.utils.useDialog();

  /*
   * data
   */

  const [ dataLoading, setDataLoading ] = useState(true);
  const [ data, setData ] = useState();

  const [ hashtags, setHashtags ] = useState('');

  useEffect(() => {

    api(token).v1.campaigns.activities
      .obtain({
        campaign: campaign.id,
        activity: activity.id,
      })
      .then(res => {
        const data = res.body.data;

        setDataLoading(false);
        setData(data);

        setHashtags(
          data.config.hashtags
          // .map(tag => `#${tag}`)
          .join(' ')
        );
      })
      .catch(err => {
        setDataLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });

  }, [token, campaign.id, activity.id]);

  /*
   * submit
   */

  const { fp } = useFPContext();

  const [ submitLoading, setSubmitLoading ] = useState(false);
  const [ submitValue, setSubmitValue ] = useState('');
  const [ customFieldValue, setCustomFieldValue ] = useState('');

  const submit = () => {

    if (data?.config?.custom_field_enabled && data?.config?.custom_field_required && !customFieldValue.trim()) {
      return;
    }

    setSubmitLoading(true);

    fp.get().then(result => {

      api(token).v1.campaigns.activities.submit
        .post(
          {
            campaign: campaign.id,
            activity: activity.id,
          },
          {
            data_json: JSON.stringify({
              value: submitValue,
              ...(data?.config?.custom_field_enabled && { custom_field: customFieldValue }),
            }),
            fingerprint: result.visitorId,
          },
        )
        .then(res => {
          setSubmitLoading(false);

          if (res.status === 200) {
            handle(res.body);
            setDialog();
          }

          if (res.status === 400) {
            toast.show(toast.KIND.ERROR, res.body);
          }
        })
        .catch(err => {
          setSubmitLoading(false);
          toast.show(toast.KIND.WARNING, err);
        });
    });
  };

  /*
   * content
   */

  return (
    <Base title='Submit'>
      <div className={styles.root}>
        {
          dataLoading &&
          <div className={styles.loading}>
            Loading...
          </div>
        }

        {
          data && (
            <>
              <div
                className={styles.instr}
                dangerouslySetInnerHTML={{
                  __html: data.instr
                }}
              />

              <div className={styles.field}>
                <label htmlFor='hashtags'>
                  Use these hashtags in your <i>Video Review</i> caption
                </label>

                <input
                  className={styles.copy}
                  id='hashtags'
                  type='text'
                  disabled={true}
                  aria-label='hashtags'
                  value={hashtags}
                />

                <button
                  type='button'
                  onClick={() => {
                    navigator.clipboard.writeText(hashtags)
                      .then(() => {
                        toast.show(toast.KIND.SUCCESS, 'Hashtags copied.');
                      })
                      .catch(() => {
                        toast.show(toast.KIND.WARNING, 'Could not copy to clipboard. Please copy manually.');
                      });
                  }}>

                  Copy Hashtags

                </button>
              </div>

              <div className={styles.field}>
                <label htmlFor='value'>
                  Paste your <i>Video Review</i> link
                  <span>Use Share &rarr; Copy link</span>
                </label>

                <input
                  id='value'
                  aria-label='link'
                  type='url'
                  disabled={submitLoading}
                  value={submitValue}
                  onChange={(event) => setSubmitValue(event.target.value)}
                />

                {data?.config?.custom_field_enabled && (
                  <>
                    <label htmlFor='custom_field'>
                      {data.config.custom_field_label}
                    </label>

                    <input
                      id='custom_field'
                      aria-label='custom field'
                      type='text'
                      disabled={submitLoading}
                      value={customFieldValue}
                      onChange={(event) => setCustomFieldValue(event.target.value)}
                    />
                  </>
                )}

                <button
                  type='button'
                  disabled={submitLoading}
                  onClick={() => submit()}>

                  Submit

                </button>
              </div>

              {data.config.sample_videos?.length > 0 && (
                <div className={styles.videos}>
                  <h3>✨ Check out these videos for inspiration! 🎬</h3>
                  {data.config.sample_videos.map((url, index) => {
                    const videoId = extractTikTokVideoId(url);
                    if (!videoId) return null;
                    return (
                      <div key={videoId}>
                        <iframe
                          src={`https://www.tiktok.com/embed/v2/${videoId}`}
                          loading={index > 0 ? 'lazy' : undefined}
                          allowFullScreen
                          allow='encrypted-media'
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )
        }
      </div>
    </Base>
  );
};

export default UrlSubmit;